package com.milta.feature.route.presentation

import com.milta.feature.route.domain.entity.Delivery
import com.milta.feature.route.domain.entity.DeliveryStatus
import com.milta.feature.route.domain.entity.DriverRouteOverview
import com.milta.feature.route.domain.entity.FailureReason
import com.milta.feature.route.domain.entity.Route
import com.milta.feature.route.domain.entity.RouteStatus
import com.milta.feature.route.domain.entity.TeamRoutesOverview

/** Which day of [WebDashboardState.overview] is shown (MIL-48, DOM-06 — Dashboard Web Supervision). */
enum class WebDashboardTab { Today, Tomorrow }

/** Ordre d'affichage des tournées (retour client) : en cours avant planifiée avant terminée — PAS
 * l'ordre naturel de [RouteStatus] (Scheduled, InProgress, Completed), qui classerait une tournée
 * en cours après les tournées encore planifiées. */
private fun routeStatusDisplayPriority(status: RouteStatus): Int = when (status) {
    RouteStatus.InProgress -> 0
    RouteStatus.Scheduled -> 1
    RouteStatus.Completed -> 2
}

/**
 * UI state for the web dashboard's Supervision screen (MIL-48, DOM-06). Snapshot state only — no
 * SignalR/polling (explicit PO decision): [overview]/[expandedRoute] only reflect the backend as of
 * the last successful load / [WebDashboardIntent.Refresh].
 *
 * [error]/[routeDetailError] hold stable error CODEs resolved to localized strings by the screen,
 * same convention as [GestionnaireDashboardState]/[GestionnaireRouteDetailState].
 *
 * Ticket requirement (MIL-48): on [WebDashboardTab.Tomorrow], NO progress/percentage/active-stop is
 * ever exposed or computed — only driver/status/hours. [globalProgress] and [routeProgressByRouteId]
 * are explicitly gated on [selectedTab] below to enforce this at the state level rather than relying
 * on the screen to simply not render them.
 */
data class WebDashboardState(
    val selectedTab: WebDashboardTab = WebDashboardTab.Today,
    val overview: TeamRoutesOverview? = null,
    val isLoading: Boolean = false,
    val error: String? = null,
    /** Écran détail (Écran ... d'arrêts) d'une tournée dépliée en place sous sa carte, MIL-38's
     * `GET /routes/{id}` ([com.milta.feature.route.domain.usecase.GetRouteDetailUseCase]). Null when
     * no route is expanded. */
    val expandedRouteId: String? = null,
    val expandedRoute: Route? = null,
    val isLoadingRouteDetail: Boolean = false,
    val routeDetailError: String? = null,
    /** Retour client 17/09/2026, point 12 — proof-photo dialog for a Delivered/Failed stop of
     * [expandedRoute]. Non-null [proofDialogDeliveryId] means the dialog is open; the photo/comment
     * fields below populate once `GET routes/{routeId}/proofs` resolves (same contract already used
     * by the web "Historique des preuves" screen). */
    val proofDialogDeliveryId: String? = null,
    val isLoadingProof: Boolean = false,
    val proofPhotoUrl: String? = null,
    val proofComment: String? = null,
    val proofFailureReason: FailureReason? = null,
    val proofLoadFailed: Boolean = false,
    /** Retour 27/10 (point 3) — the proof was validated without photo (driver's own route). */
    val proofWithoutPhoto: Boolean = false,
    // ── Filtre statut + chauffeur (MIL-85) — purement client, appliqué sur [overview] déjà chargé ──
    val statusFilter: RouteStatus? = null,
    val driverFilter: String? = null,
) {
    private val routes: List<DriverRouteOverview> get() = overview?.routes.orEmpty()

    /** [routes] après application de [statusFilter]/[driverFilter], triées par priorité d'attention
     * plutôt que par ordre d'arrivée backend — jamais les KPI ci-dessous (comptes globaux de la
     * journée, non affectés par le filtre/tri d'affichage de la liste). Retour client : une tournée
     * en cours doit se voir en premier, avant les tournées encore planifiées. */
    val visibleRoutes: List<DriverRouteOverview>
        get() = routes
            .filter { route ->
                (statusFilter == null || route.status == statusFilter) &&
                    (driverFilter == null || route.driverId == driverFilter)
            }
            // MIL-91 — En cours > Planifié > Terminé, then driver name A→Z (case-insensitive, same
            // "first last" string as the card shows); sortedWith is stable for ties.
            .sortedWith(
                compareBy<DriverRouteOverview>(
                    { routeStatusDisplayPriority(it.status) },
                    { "${it.driverFirstName} ${it.driverLastName}".trim().lowercase() },
                ),
            )

    /** Chauffeurs distincts du jour affiché, pour peupler le filtre chauffeur — dérivé de [routes]
     * (non filtré), pas de [visibleRoutes] : sinon sélectionner un statut masquerait les chauffeurs
     * n'ayant aucune tournée dans ce statut, rendant le filtre chauffeur lui-même inutilisable. */
    val availableDrivers: List<DriverRouteOverview>
        get() = routes.distinctBy { it.driverId }

    // --- KPI (comptes réels, pas d'approximation) — affichés sur les deux onglets, ce ne sont
    // pas des pourcentages ni une notion d'arrêt actif, donc pas concernés par l'interdiction
    // ci-dessus sur Tomorrow.
    val inProgressRoutesCount: Int get() = routes.count { it.status == RouteStatus.InProgress }
    val scheduledRoutesCount: Int get() = routes.count { it.status == RouteStatus.Scheduled }
    val completedRoutesCount: Int get() = routes.count { it.status == RouteStatus.Completed }

    /**
     * Ratio global `completedDeliveries / totalDeliveries` du jour affiché. Null tant qu'aucun
     * [overview] n'est chargé, s'il n'y a rien à diviser (0 livraison), OU sur
     * [WebDashboardTab.Tomorrow] (exigence ticket : aucune progression pour demain).
     */
    val globalProgress: Float?
        get() {
            if (selectedTab == WebDashboardTab.Tomorrow) return null
            val ov = overview ?: return null
            return if (ov.totalDeliveries == 0) null else ov.completedDeliveries.toFloat() / ov.totalDeliveries
        }

    /**
     * Pourcentage par tournée, [DriverRouteOverview.progress] réutilisé tel quel (pas recalculé),
     * indexé par `routeId`. Null sur [WebDashboardTab.Tomorrow] (aucun pourcentage exposé — exigence
     * ticket) ; jamais recalculé différemment de l'entité domaine sur Today.
     */
    val routeProgressByRouteId: Map<String, Float>?
        get() = if (selectedTab == WebDashboardTab.Tomorrow) null else routes.associate { it.routeId to it.progress }

    /**
     * Détail des arrêts de [expandedRoute], enrichi du statut d'affichage à 5 valeurs
     * ([WebDashboardStopDisplayStatus] — voir [deliveryDisplayStatus]). Null tant qu'aucune tournée
     * n'est dépliée ou que son détail n'a pas encore été chargé. Pas de gate explicite sur
     * [selectedTab] ici : une tournée du lendemain est forcément [RouteStatus.Scheduled], donc
     * [Route.currentDeliveryId] (réutilisé par [deliveryDisplayStatus]) y vaut déjà toujours `null`
     * — l'état "En route" ne peut structurellement pas apparaître pour Tomorrow, sans code dédié.
     */
    val expandedStops: List<WebDashboardStopDisplay>?
        get() {
            val route = expandedRoute ?: return null
            return route.deliveries.map { delivery ->
                WebDashboardStopDisplay(delivery = delivery, displayStatus = deliveryDisplayStatus(delivery, route))
            }
        }
}

/**
 * Statut d'affichage d'un arrêt sur le détail du dashboard web (MIL-48) — 5 valeurs, PAS 3 : distinct
 * du mobile [StopDisplayStatus] (4 valeurs, [DeliveryStatus.Untreated] non distingué) car le ticket
 * exige que "Non traité" reste séparé de "Non livré". Libellés/icônes (✓/→/○/✗/⊘) définis dans le
 * mockup — la traduction finale (Strings.kt + 9 fichiers) est laissée à l'écran Compose HTML
 * consommateur, une tâche i18n séparée validera le libellé exact de [Failed] vs le mobile "Échec".
 */
enum class WebDashboardStopDisplayStatus {
    /** [DeliveryStatus.Completed] -> "Livré" (✓). */
    Delivered,

    /** Premier [DeliveryStatus.Pending] dans l'ordre des arrêts ([Route.currentDeliveryId]) -> "En route" (→). */
    EnRoute,

    /** [DeliveryStatus.Pending] suivants -> "En attente" (○). */
    Waiting,

    /** [DeliveryStatus.Failed] -> "Non livré" (✗) — état distinct de [Untreated]. */
    Failed,

    /** [DeliveryStatus.Untreated] -> "Non traité" (⊘) — état distinct de [Failed]. */
    Untreated,
}

/** Un arrêt du détail de tournée déplié, avec son statut d'affichage déjà résolu. */
data class WebDashboardStopDisplay(
    val delivery: Delivery,
    val displayStatus: WebDashboardStopDisplayStatus,
)

/**
 * Mapping [DeliveryStatus] (+ [Route.currentDeliveryId], le "stop actif" déjà calculé côté domaine,
 * mirrors [RouteSummary.currentStopId]/[StopDetailViewModel]'s own `isCurrent`) -> statut d'affichage
 * à 5 valeurs. Ne réinvente pas la règle de "stop actif" : [route]'s [Route.currentDeliveryId] est
 * réutilisé tel quel, purement dérivé, jamais persisté par arrêt côté backend.
 */
fun deliveryDisplayStatus(delivery: Delivery, route: Route): WebDashboardStopDisplayStatus = when {
    delivery.status == DeliveryStatus.Completed -> WebDashboardStopDisplayStatus.Delivered
    delivery.status == DeliveryStatus.Failed -> WebDashboardStopDisplayStatus.Failed
    delivery.status == DeliveryStatus.Untreated -> WebDashboardStopDisplayStatus.Untreated
    delivery.id == route.currentDeliveryId -> WebDashboardStopDisplayStatus.EnRoute
    else -> WebDashboardStopDisplayStatus.Waiting
}
